import { Component, OnDestroy } from '@angular/core';
import { MatLegacySnackBar as MatSnackBar } from '@angular/material/legacy-snack-bar';
import { ICellEditorAngularComp } from 'ag-grid-angular';
import { ICellEditorParams } from 'ag-grid-enterprise';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { finalize, takeUntil } from 'rxjs/operators';

import { SnackBarConfig } from '../../configs/snackbar.config';
import { OrderApiService } from '../../services/api/order/order-api.service';

export interface IAgGridEditorDateParams extends ICellEditorParams {
  save ?:(params :IAgGridEditorDateParams, value :string) => Observable<any>;
}

@Component({
  selector: 'lum-ag-grid-editor-date',
  templateUrl: './ag-grid-editor-date.component.html',
  styleUrls: ['./ag-grid-editor-date.component.scss'],
})
export class AgGridEditorDateComponent
  implements ICellEditorAngularComp, OnDestroy
{
  readonly #loading$ = new BehaviorSubject<boolean>(false);
  readonly #onDestroy$ = new Subject<void>();

  loading$ = this.#loading$.asObservable();

  cellName :string = 'the cell value';
  latestValidValue :string;
  params :IAgGridEditorDateParams = null;
  value :Date;

  loaderDiameter :number = 16;

  constructor(private snackBar :MatSnackBar) {}

  ngOnDestroy() :void {
    this.#onDestroy$.next();
    this.#onDestroy$.complete();
  }

  agInit(params :IAgGridEditorDateParams) :void {
    this.params = params;

    this.#setValues();

    if (this.params.colDef?.headerName) {
      this.cellName = this.params.colDef.headerName;
    }
  }

  getValue() :string {
    return this.value?.toISOString() ?? '';
  }

  handlePickerToggleClick(e :Event, datepicker) {
    e.preventDefault();

    datepicker.open();
  }

  handleSubmit(e :Event) {
    e.preventDefault();

    if (this.value?.valueOf() === new Date(this.latestValidValue).valueOf()) {
      this.#stopEditing();
    }

    this.#save();
  }

  handleDateChange() :void {
    this.#save();
  }

  handleInputChange(event :Event) :void {
    const inputValue = (event.target as HTMLInputElement).value;
    if (!inputValue) {
      this.value = null;
      this.#save();
    }
  }

  #save() {
    if (typeof this.params?.save !== 'function') {
      return;
    }

    this.#loading$.next(true);

    this.params
      .save(this.params, this.getValue())
      .pipe(
        takeUntil(this.#onDestroy$),
        finalize(() => {
          this.#loading$.next(false);
          this.#stopEditing();
        }),
      )
      .subscribe({
        next: () => {
          this.params.value = this.getValue();
          this.#setValues();
          this.snackBar.open(`${this.cellName} updated.`, 'Success', {
            duration: SnackBarConfig.DEFAULT.duration,
          });
        },
        error: () => {
          this.params.value = this.latestValidValue; // revert
          this.#setValues();
          this.snackBar.open(`Failed to update ${this.cellName}.`, 'Error', {
            duration: SnackBarConfig.DEFAULT.duration,
          });
        },
      });
  }

  #setValues() {
    if (!this.params) {
      return;
    }

    this.latestValidValue = this.params.value;
    this.value = this.params.value ? new Date(this.params.value) : null;
  }

  #stopEditing() {
    this.params.stopEditing();
    this.params.api.stopEditing();
  }
}
